import { useEffect, useRef, useState } from 'react';

import { setAccessToken } from '@/lib/api/client';
import { mfaResendApi, mfaVerifyApi } from '@/lib/auth/authApi';
import { useAuthStore } from '@/stores/auth-store';
import { CircleDot, Loader2, Mail, ShieldCheck, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

interface Challenge {
  token: string;
  type: string;
  methodsAllowed: string[];
  obfuscatedEmail: string;
}

interface Props {
  challenge: Challenge;
  onSuccess: () => void;
  onCancel: () => void;
}

const MFA_ERROR_MESSAGES: Record<string, string> = {
  MFA_CODE_INVALID: 'Code incorrect. Vérifiez et réessayez.',
  MFA_TOO_MANY_ATTEMPTS: 'Trop de tentatives. Veuillez réessayer plus tard.',
  MFA_CHALLENGE_EXPIRED: 'Le code a expiré. Retournez à la page de connexion.',
};

export default function MfaChallengeScreen({ challenge, onSuccess, onCancel }: Props) {
  const { login } = useAuthStore();

  const [code, setCode] = useState('');
  // Ne jamais afficher l'écran "app d'authentification" tant que le TOTP n'est pas
  // réellement proposé par le backend. Si methodsAllowed est vide/inattendu, on
  // retombe sur l'email OTP (2e facteur par défaut des comptes sans app TOTP),
  // jamais sur 'totp'.
  const [method, setMethod] = useState<string>(() =>
    challenge.methodsAllowed.includes('totp') ? 'totp' : (challenge.methodsAllowed[0] ?? 'email_otp'),
  );
  const [trustDevice, setTrustDevice] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // deviceToken généré une seule fois et stable
  const deviceTokenRef = useRef<string | undefined>(undefined);

  const isBackupCode = method === 'backup_code';
  const isEmailOtp = method === 'email_otp';
  const isTotp = method === 'totp';

  // Timer cooldown renvoi
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Générer le deviceToken si trustDevice coché et pas encore créé
      if (trustDevice && !deviceTokenRef.current) {
        deviceTokenRef.current = crypto.randomUUID();
      }

      const result = await mfaVerifyApi({
        challengeToken: challenge.token,
        method,
        code: code.trim(),
        trustDevice: trustDevice || undefined,
        deviceToken: trustDevice ? deviceTokenRef.current : undefined,
        deviceLabel: trustDevice ? navigator.userAgent.slice(0, 100) : undefined,
      });

      setAccessToken(result.accessToken);
      login(result.user, result.accessToken);
      toast.success('Connexion réussie');
      onSuccess();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(MFA_ERROR_MESSAGES[msg] ?? msg);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleResend() {
    if (resendCooldown > 0) return;
    setIsResending(true);
    try {
      await mfaResendApi(challenge.token);
      setResendCooldown(60);
      toast.success('Code renvoyé');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur lors du renvoi');
    } finally {
      setIsResending(false);
    }
  }

  function switchToBackupCode() {
    setMethod('backup_code');
    setCode('');
    setError(null);
  }

  function switchToTotp() {
    setMethod('totp');
    setCode('');
    setError(null);
  }

  const codeMaxLength = isBackupCode ? 8 : 6;
  const codePlaceholder = isBackupCode ? 'XXXXXXXX' : '000000';

  return (
    <section className='min-h-screen bg-gradient-to-br from-emerald-900 to-emerald-500 flex flex-col items-center justify-center px-6 pt-20 pb-10'>
      {/* Brand */}
      <div className='flex items-center gap-3 mb-8'>
        <CircleDot className='w-10 h-10 text-white' />
        <span className='text-2xl font-extrabold text-white'>BoulesConnect</span>
      </div>

      {/* Card */}
      <div className='w-full max-w-md bg-white rounded-3xl p-8 shadow-xl'>
        <div className='flex flex-col items-center mb-6'>
          <div className='flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 mb-4'>
            <ShieldCheck className='h-7 w-7 text-emerald-600' />
          </div>
          <h2 className='text-2xl font-extrabold text-slate-900 text-center'>Vérification en 2 étapes</h2>

          {/* Method badge */}
          {isEmailOtp && (
            <div className='mt-4 flex flex-col items-center gap-2'>
              <span className='inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-200 px-4 py-2 text-sm font-semibold text-blue-700'>
                <Mail className='w-4 h-4' />
                Via email
              </span>
              <p className='text-sm text-slate-500 text-center'>
                Code envoyé à <span className='font-semibold text-slate-700'>{challenge.obfuscatedEmail}</span>
              </p>
            </div>
          )}
          {isTotp && (
            <div className='mt-4 flex flex-col items-center gap-2'>
              <span className='inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-4 py-2 text-sm font-semibold text-emerald-700'>
                <Smartphone className='w-4 h-4' />
                Via votre app
              </span>
              <p className='text-sm text-slate-500 text-center'>
                Ouvrez votre application d&apos;authentification et saisissez le code affiché.
              </p>
            </div>
          )}
          {isBackupCode && (
            <div className='mt-4 flex flex-col items-center gap-2'>
              <span className='inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-4 py-2 text-sm font-semibold text-amber-700'>
                <ShieldCheck className='w-4 h-4' />
                Code de secours
              </span>
              <p className='text-sm text-slate-500 text-center'>
                Entrez l&apos;un de vos codes de secours (8 caractères).
              </p>
            </div>
          )}
        </div>

        {/* Error banner */}
        {error && (
          <div className='mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700'>{error}</div>
        )}

        <form onSubmit={handleSubmit} className='space-y-5'>
          {/* Code input */}
          <div>
            <label htmlFor='mfa-code' className='block text-sm font-semibold text-slate-700 mb-2'>
              {isBackupCode ? 'Code de secours' : 'Code de vérification'}
            </label>
            <input
              id='mfa-code'
              type='text'
              inputMode='numeric'
              autoComplete='one-time-code'
              required
              value={code}
              onChange={e => setCode(e.target.value.replace(/\s/g, ''))}
              maxLength={codeMaxLength}
              placeholder={codePlaceholder}
              className='w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-center text-xl font-mono tracking-widest placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors'
            />
          </div>

          {/* Trust device */}
          <label className='flex items-center gap-2 cursor-pointer'>
            <input
              type='checkbox'
              checked={trustDevice}
              onChange={e => setTrustDevice(e.target.checked)}
              className='w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500'
            />
            <span className='text-sm text-slate-600'>Faire confiance à cet appareil</span>
          </label>

          {/* Submit */}
          <button
            type='submit'
            disabled={isSubmitting || code.length < codeMaxLength}
            className='w-full bg-emerald-600 text-white cursor-pointer font-bold py-3 rounded-xl hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2'
          >
            {isSubmitting && <Loader2 className='w-4 h-4 animate-spin' />}
            {isSubmitting ? 'Vérification…' : 'Vérifier'}
          </button>
        </form>

        {/* Actions secondaires */}
        <div className='mt-5 space-y-3 border-t border-slate-100 pt-5'>
          {/* Renvoyer code email OTP */}
          {isEmailOtp && (
            <button
              type='button'
              onClick={handleResend}
              disabled={isResending || resendCooldown > 0}
              className='w-full text-sm font-semibold text-emerald-600 hover:text-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1'
            >
              {isResending && <Loader2 className='w-3 h-3 animate-spin' />}
              {resendCooldown > 0 ? `Renvoyer dans ${resendCooldown}s` : 'Renvoyer le code'}
            </button>
          )}

          {/* Basculer vers backup code */}
          {!isBackupCode && challenge.methodsAllowed.includes('backup_code') && (
            <button
              type='button'
              onClick={switchToBackupCode}
              className='w-full text-sm text-slate-500 hover:text-slate-700 transition-colors'
            >
              Utiliser un code de secours
            </button>
          )}

          {/* Revenir à TOTP depuis backup_code */}
          {isBackupCode &&
            (challenge.methodsAllowed.includes('totp') || challenge.methodsAllowed.includes('email_otp')) && (
              <button
                type='button'
                onClick={switchToTotp}
                className='w-full text-sm text-slate-500 hover:text-slate-700 transition-colors'
              >
                Utiliser {challenge.methodsAllowed.includes('totp') ? "l'application" : 'le code email'}
              </button>
            )}

          {/* Annuler */}
          <button
            type='button'
            onClick={onCancel}
            className='w-full text-sm text-slate-400 hover:text-slate-600 transition-colors'
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    </section>
  );
}
